import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';

import themes, { THEME_TYPES } from 'Styles/Themes';

import { useUser, setUserInfo, removeAllUserState, removeUserInLocalStorage } from '@Services/User';
import { removeProjectsInLocalStorage } from '@Services/Projects';
import { getAuthor } from '@Services/User/Request';

import CookieImage from '@Assets/Images/ic_cookie.png';

import DefaultProfileImage from '@Assets/Images/profile_default.png';
import { ReactComponent as CameraSVG } from '@Assets/Svgs/ic_camera.svg';
import TX from '@Components/Common/Text';
import Button from '@Components/Common/Button';

import ProfileImageModal from './ProfileImageModal';

import { PROFILE_IMAGE_BASE_URL } from './ProfileImageList';

const DEFAULT_PROFILE_IMAGE_ID = '0';

const STATUS_CODE = {
  CREATE_AUTHOR_NEEDED: 400,
};

const UserInfo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { nickName, email, sponsoredCookie, likeCount, profileImageId } = useSelector(useUser);

  const [isProfileImageModalVisible, setIsProfileImageModalVisible] = useState(false);
  const theme = themes[THEME_TYPES.LIGHT];

  const dispatchRemoveAllUserState = useCallback(() => {
    dispatch(removeAllUserState());
  }, [dispatch]);

  const setAuthor = useCallback(async () => {
    try {
      const user = await getAuthor();
      dispatch(setUserInfo({ user }));
    } catch (error) {
      const { status } = error;

      if (status === STATUS_CODE.CREATE_AUTHOR_NEEDED) {
        navigate('/nickname', { replace: true });
      }
    }
  }, [dispatch]);

  const handleProfileImageModalVisible = useCallback(() => {
    setIsProfileImageModalVisible(!isProfileImageModalVisible);
  }, [isProfileImageModalVisible]);

  const handleLogOutButtonClick = () => {
    dispatchRemoveAllUserState();
    removeUserInLocalStorage();
    removeProjectsInLocalStorage();

    navigate('/login', { replace: true });
  };

  useEffect(() => {
    setAuthor();
  }, []);

  return (
    <Container>
      <ProfileImageContainer onClick={handleProfileImageModalVisible}>
        <ProfileImage imageId={profileImageId} />
        <SelectProfileImageWrapper>
          <CameraSVG />
        </SelectProfileImageWrapper>
      </ProfileImageContainer>

      <UserInfoContainer>
        <UserAccountContainer>
          <UserIdText>{nickName}</UserIdText>
          <UserEmailText>{email}</UserEmailText>
        </UserAccountContainer>

        <ProjectInfoContainer>
          <ProjectInfoText>후원</ProjectInfoText>
          <ProjectInfoText isLightGrey>{sponsoredCookie}</ProjectInfoText>
          <SupportCookieIcon />

          <ProjectInfoText>좋아요</ProjectInfoText>
          <ProjectInfoText isLightGrey>{likeCount}</ProjectInfoText>
        </ProjectInfoContainer>
      </UserInfoContainer>

      <Button
        text="로그아웃"
        backgroundColor="transparent"
        textColor={theme.colors.ui_on_1}
        onClick={handleLogOutButtonClick}
      />

      {isProfileImageModalVisible && (
        <ProfileImageModal
          isProfileImageModalVisible={isProfileImageModalVisible}
          handleProfileImageModalVisible={handleProfileImageModalVisible}
          setAuthor={setAuthor}
        />
      )}
    </Container>
  );
};

const FlexAlignCenterDiv = styled.div`
  display: flex;
  align-items: center;
`;

const Container = styled(FlexAlignCenterDiv)`
  justify-content: space-between;

  width: 100%;
  height: 80px;

  padding-right: 24px;
  margin-bottom: 48px;
`;

const ProfileImageContainer = styled.div`
  position: relative;

  width: 80px;
  height: 80px;

  margin-right: 26px;
  overflow: hidden;
  border-radius: 72px;
  border: 1px solid ${({ theme }) => theme.colors.ui_3};
  cursor: pointer;
`;

const ProfileImage = styled.img.attrs(({ imageId }) => ({
  src:
    imageId === DEFAULT_PROFILE_IMAGE_ID || imageId === undefined
      ? DefaultProfileImage
      : `${PROFILE_IMAGE_BASE_URL}/${imageId}.png`,
}))`
  width: 100%;
  height: 100%;
`;

const SelectProfileImageWrapper = styled(FlexAlignCenterDiv)`
  justify-content: center;

  width: 100%;
  height: 24px;

  position: absolute;
  bottom: 0px;

  background-color: rgba(10, 10, 10, 0.4);
`;

const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  flex: 1;
  height: 100%;
`;

const UserAccountContainer = styled.div``;

const UserIdText = styled(TX.Title1)`
  color: ${({ theme }) => theme.colors.ui_on_1};
`;

const UserEmailText = styled(TX.Caption1)`
  display: block;

  color: ${({ theme }) => theme.colors.ui_on_3};
`;

const ProjectInfoContainer = styled(FlexAlignCenterDiv)``;

const ProjectInfoText = styled(TX.FootnoteM)`
  letter-spacing: -0.3px;

  ${({ isLightGrey, theme }) => css`
    color: ${isLightGrey && theme.colors.ui_on_2};
    margin: ${isLightGrey && '0px 4px 0px 8px'};
  `}
`;

const SupportCookieIcon = styled.img.attrs({
  src: CookieImage,
  alt: '후원 수',
})`
  width: 20px;
  height: 20px;

  margin-right: 14px;
`;

export default UserInfo;
